import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';

/**
 * Routes not gated by the onboarding_step redirect below — a user who
 * hasn't verified their email or has no property yet must still be able
 * to reach these two (see App\Http\Controllers\Api\V1\AuthController::
 * userPayload()'s onboarding_step values).
 */
const ONBOARDING_EXEMPT_PATHS = ['/onboarding', '/properties/new'];

export function ProtectedRoute() {
  const { user, isInitializing } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (isInitializing) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Stack role="status" spacing={2} sx={{ alignItems: 'center' }}>
          <CircularProgress size={28} />
          <Typography color="text.secondary">{t('shell.checkingSession')}</Typography>
        </Stack>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Only an *unverified* user is forced out of the app shell — there is
  // genuinely nothing they can do until they verify. A verified user who
  // simply has no property yet is allowed straight into the dashboard,
  // which greets them with a "get started" state (create a property,
  // complete their profile), and property-scoped pages fall back to their
  // own empty states via RequireProperty.
  if (
    user.onboarding_step === 'verify_email' &&
    !ONBOARDING_EXEMPT_PATHS.includes(location.pathname)
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}
